import React, { useState } from 'react';
import { useRouter } from 'hooks/use-router';
import { Layout } from './admin-activity-log-detail.styled';
import useAppBarTitle from 'hooks/use-app-bar-title';
import { Divider, Grid } from '@material-ui/core';
import PageHeader from 'components/layout/page-header/page-header';
import { Root } from '../admin-activity-log-list/admin-activity-log-list.styled';
import useAdminActivityLogDetail from '../../hooks/use-admin-activity-log-detail';
import { TGetAdminActivityLogDetailResponse } from 'types/entities/admin-activity-logs';
import {
    Box,
    BoxContent,
    BoxTitle,
} from 'components/layout/box/custom-box.styled';
import { toSentenceCaseFormat } from 'utils/sentence-case-format';
import PendingTaskReadableStatus from 'app/private/containers/pending-tasks/components/PendingTaskReadableStatus';
import { ERoutesPrivate, ERoutesType, routes } from 'types/routes';
import timeFormat from 'utils/time-format';

type TAdminActivityLogDetailQuery = {
    id: string;
};

const pageTitle = 'Admin Activity Log Details';

const AdminActivityLogDetail: React.FC = () => {
    useAppBarTitle(pageTitle);
    const { history, query } = useRouter<TAdminActivityLogDetailQuery>();

    const [curUserData, setCurUserData] =
        useState<TGetAdminActivityLogDetailResponse | null>();

    const { isLoading, isError, error } = useAdminActivityLogDetail(query.id, {
        onSuccess: (data) => {
            setCurUserData(data as TGetAdminActivityLogDetailResponse);
        },
    });

    const handleUserClick = (userId: string) => {
        history.push(
            `${routes[ERoutesType.Private][ERoutesPrivate.Users][0]}/${userId}`,
        );
    };

    if (isLoading) {
        return <>Loading</>;
    }

    if (isError || !curUserData) {
        return <>{error}</>;
    }

    return (
        <Root>
            <Layout>
                <PageHeader />
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Box>
                            <BoxTitle>Admin Activity Log</BoxTitle>
                            <BoxContent>
                                <div>
                                    <span>Log ID</span>
                                    <span>{curUserData.id}</span>
                                </div>
                                <Divider />
                                <div>
                                    <span>Admin User</span>
                                    <span>{curUserData.adminEmail}</span>
                                </div>
                                <Divider />
                                <div>
                                    <span>Action Type</span>
                                    <span>
                                        {toSentenceCaseFormat(
                                            curUserData.actionType,
                                        )}
                                    </span>
                                </div>
                                <Divider />
                                <div>
                                    <span>Action Level</span>
                                    <span>
                                        {toSentenceCaseFormat(
                                            curUserData.actionLevel,
                                        )}
                                    </span>
                                </div>
                                <Divider />
                                <div>
                                    <span>User ID</span>
                                    <span>{curUserData.userId || '-'}</span>
                                </div>
                                <Divider />
                                <div>
                                    <span>User Nex Tag</span>
                                    <span>
                                        {curUserData.nexId
                                            ? `@${curUserData.nexId}`
                                            : '-'}
                                    </span>
                                </div>
                                <Divider />
                                <div>
                                    <span>Pending Task ID</span>
                                    <span>
                                        {curUserData.pendingTaskId || '-'}
                                    </span>
                                </div>
                                <Divider />
                                <div>
                                    <span>Pending Task Status</span>
                                    <span>
                                        {curUserData.pendingTaskStatus ? (
                                            <PendingTaskReadableStatus
                                                rawStatus={
                                                    curUserData.pendingTaskStatus
                                                }
                                            />
                                        ) : (
                                            '-'
                                        )}
                                    </span>
                                </div>
                                <Divider />
                                <div>
                                    <span>Created At</span>
                                    <span>
                                        {timeFormat(curUserData.createdAt) ||
                                            '-'}
                                    </span>
                                </div>
                                <Divider />
                                {curUserData.details.map((detail) => {
                                    return (
                                        <>
                                            <div>
                                                <span>
                                                    {toSentenceCaseFormat(
                                                        detail.name,
                                                    )}
                                                </span>
                                                <span>{detail.value}</span>
                                            </div>
                                            <Divider />
                                        </>
                                    );
                                })}
                            </BoxContent>
                        </Box>
                    </Grid>
                </Grid>
            </Layout>
        </Root>
    );
};

export default AdminActivityLogDetail;
